import { Css, ScrollableParent, useBreakpoint, zIndices } from "@homebound/beam";
import { PropsWithChildren } from "react";
import { SideNav, SideNavProps, sideNavCollapsedWidth } from "src/components/layout/SideNav";
import { SideNavContextProvider, useSideNavContext } from "src/components/layout/SideNavContext";
import { topNavHeight } from "src/routes/layout/global-nav/GlobalNav";

type SideNavLayoutProps = {
  sideNavProps: SideNavProps;
  layoutProps?: { contentCss: Parameters<typeof ScrollableParent>["0"]["xss"] };
};

export function SideNavLayout(props: PropsWithChildren<SideNavLayoutProps>) {
  return (
    <SideNavContextProvider>
      <SideNavLayoutContent {...props} />
    </SideNavContextProvider>
  );
}

function SideNavLayoutContent(props: PropsWithChildren<SideNavLayoutProps>) {
  const { children, sideNavProps, layoutProps } = props;
  const { sideNavState, setSideNavState } = useSideNavContext();
  const bp = useBreakpoint();

  return (
    <div css={Css.df.oh.fg1.$}>
      <SideNav {...sideNavProps} />
      {/* When the side-nav is expanded, and we are at the medium breakpoint and down,
       then provide an overlay when clicked/touched will close the side nav */}
      {sideNavState === "expanded" && bp.mdAndDown && (
        <div
          css={
            Css.absolute.right0.bottom0.left0
              .topPx(topNavHeight)
              .add("backgroundColor", "rgba(36,36,36,0.2)")
              // Ensure the overlay is below the side nav. FWIW, Using one-off value as `SideNavLayout` will be replaced with a Beam layout in the future, and mobile is expected to be handled very differently.
              .add("zIndex", zIndices.sideNav - 1).$
          }
          onClick={() => setSideNavState("collapse")}
          aria-label="Close navigation"
        />
      )}
      <ScrollableParent
        px={3}
        xss={{
          ...Css.fg1.$,
          // The side nav uses a `position: absolute` when `bp.mdAndDown` is true. Add extra margin to account for this.
          ...(bp.mdAndDown ? Css.mlPx(sideNavCollapsedWidth).$ : {}),
          ...(layoutProps?.contentCss || {}),
        }}
      >
        {children}
      </ScrollableParent>
    </div>
  );
}
